/**
 * Validates a Content Hub URL.
 * Allows empty strings. Enforces strict HTTP/HTTPS, valid TLDs, and rejects IP addresses.
 * @param {string} input URL string to validate
 * @returns {boolean} true if valid, false otherwise
 */
export function validateContentHubUrl(input: string): boolean {
  if (input === "") return true;
  if (input !== input.trim()) return false;

  // Regex Breakdown:
  // ^https?:\/\/  -> Must start with http:// or https://
  // (?!\/)        -> Negative lookahead: the next character must NOT be a slash.
  //                  (Prevents "https:///..." which bypasses standard parsers)
  if (!/^https?:\/\/(?!\/)/.test(input)) return false;

  let u: URL;
  try {
    u = new URL(input);
  } catch {
    return false;
  }

  if (u.protocol !== "http:" && u.protocol !== "https:") return false;

  const hostname = u.hostname;

  // Validates a hostname (e.g., "sub.example-domain.com") ensuring that:
  // 1. Segments are separated by dots.
  // 2. Each segment starts & ends with a lowercase alphanumeric char.
  // 3. Hyphens are allowed only inside segments (not at the start/end).
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/.test(hostname)) {
    return false;
  }

  const parts = hostname.split(".");

  // Must look like a domain (e.g., "google.com")
  if (parts.length < 2) return false;

  // Detects ".." and trailing dots
  if (parts.some((part) => part === "")) return false;

  const tld = parts.at(-1);
  if (!tld) return false;

  // The Top-Level Domain (TLD) can't be a number
  if (/^\d+$/.test(tld)) return false;

  if (tld === "*") return false;

  // There are no 1 character TLDs.
  // Check: https://data.iana.org/TLD/tlds-alpha-by-domain.txt
  if (tld.length < 2) return false;

  return true;
}

/**
 * Strips the schema (http:// or https://) from a website string.
 * @param {string} website website string to strip schema from
 * @returns string
 */
export function stripSchema(website: string): string {
  const schemeSeparator = "://";

  const schemeStartIndex = website.indexOf(schemeSeparator);

  if (schemeStartIndex !== -1) {
    return website.substring(schemeStartIndex + schemeSeparator.length);
  } else {
    return website;
  }
}

/**
 * Given a schema stripped url, this function will transform the domain to lower case.
 * Return the original string if it can't be parsed.
 * @param {string} url url string to downcase domain
 * @returns string
 */
export function downcaseDomain(url: string): string {
  const parsedUrl = url.match(/([^/]+)(\S*)/);

  if (!parsedUrl) {
    return url;
  }

  return `${parsedUrl[1].toLowerCase()}${parsedUrl[2]}`;
}

/**
 * Automatically tries to assume the website string that is entered.
 * Adds a ".com" if no ending extension is present.
 * @param {string} website website string to fix
 * @returns string
 */
export function fixWebsiteString(website: string): string {
  if (website.length === 0) {
    return website;
  }
  const spacelessString = website.replace(/\s/g, "_");
  const normalizedString = downcaseDomain(spacelessString);
  if (normalizedString[normalizedString.length - 1] === ".") {
    return `${normalizedString.substr(0, normalizedString.length - 1)}.com`;
  }
  if (normalizedString.indexOf(".") !== -1 && normalizedString[normalizedString.length - 1] !== ".") {
    return normalizedString;
  }
  return `${normalizedString}.com`;
}
